import {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useRef,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { backendUri } from 'helpers/auth/openid/openid-settings';
import { constantSessionId } from 'helpers/sessionProvider';

export const EnableSignalRDebug = false;

export interface SignalRConnectionData {
  connection: signalR.HubConnection;
  componentsSubscribed: number;
  connectedListeners: Set<Dispatch<SetStateAction<boolean>>>;
}

function typedKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

function isConnectionAlive(connectionState: signalR.HubConnectionState) {
  return (
    connectionState !== signalR.HubConnectionState.Disconnected &&
    connectionState !== signalR.HubConnectionState.Disconnecting
  );
}

export const SignalRConnectionsProvider = (props: React.PropsWithChildren) => {
  const connectionsStore = useRef<Map<string, SignalRConnectionData>>(
    new Map(),
  );

  function createConnection(
    hubName: string,
    queryParameters?: string[],
    setIsConnected?: Dispatch<SetStateAction<boolean>>,
  ) {
    let connectionData = connectionsStore.current.get(hubName);

    if (!connectionData) {
      const allQueryParameters = [
        `constantSessionId=${encodeURIComponent(constantSessionId)}`,
        ...(queryParameters ?? []),
      ];

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(
          backendUri + '/hubs/' + hubName + '?' + allQueryParameters.join('&'),
          {
            withCredentials: true,
          },
        )
        .configureLogging(
          EnableSignalRDebug
            ? signalR.LogLevel.Information
            : signalR.LogLevel.None,
        )
        .withAutomaticReconnect()
        .build();

      const connectedListeners = new Set<Dispatch<SetStateAction<boolean>>>();
      newConnection.onreconnected(() =>
        connectedListeners.forEach((listener) => listener(true)),
      );

      connectionData = {
        connection: newConnection,
        componentsSubscribed: 0,
        connectedListeners,
      };

      connectionsStore.current.set(hubName, connectionData);
    }

    if (setIsConnected) {
      connectionData.connectedListeners.add(setIsConnected);
      if (
        connectionData.connection.state === signalR.HubConnectionState.Connected
      )
        setIsConnected(true);
    }

    if (!isConnectionAlive(connectionData.connection.state)) {
      const { connectedListeners } = connectionData;
      connectionData.connection
        .start()
        .then(() => {
          connectedListeners.forEach((listener) => listener(true));
        })
        .catch(() => (EnableSignalRDebug ? console.error : null));
    }

    return connectionData;
  }

  function setupEventHandlers<T>(
    hubName: string,
    handlers: Partial<T>,
    queryParameters?: string[],
  ) {
    let connectionData = connectionsStore.current.get(hubName);
    if (!connectionData) {
      connectionData = createConnection(hubName, queryParameters);
    }

    for (const key of typedKeys(handlers)) {
      const value = handlers[key];
      if (value !== undefined) {
        connectionData.connection.on(
          key as string,
          value as (...args: any[]) => any,
        );
      }
    }

    connectionData.componentsSubscribed++;
  }

  function deleteConnection<T>(
    hubName: string,
    handlers: Partial<T>,
    setIsConnected?: Dispatch<SetStateAction<boolean>>,
  ) {
    const connectionData = connectionsStore.current.get(hubName);
    if (!connectionData) return;

    for (const key of typedKeys(handlers)) {
      const value = handlers[key];
      if (value !== undefined) {
        connectionData.connection.off(
          key as string,
          value as (...args: any[]) => any,
        );
      }
    }

    if (setIsConnected)
      connectionData.connectedListeners.delete(setIsConnected);

    connectionData.componentsSubscribed--;

    if (connectionData.componentsSubscribed === 0) {
      void connectionData.connection
        .stop()
        .then(() => {
          setIsConnected?.(false);
        })
        .catch(() => (EnableSignalRDebug ? console.error : null));
      connectionsStore.current.delete(hubName);
    }
  }

  return (
    <SignalRConnectionsContext.Provider
      value={{
        createConnection,
        setupEventHandlers,
        deleteConnection,
        connectionsStore,
      }}
    >
      {props.children}
    </SignalRConnectionsContext.Provider>
  );
};

export const useSignalRConnections = () => {
  const context = useContext(SignalRConnectionsContext);
  if (!context) {
    throw new Error(
      'useSignalRConnectionsContext must be used within a SignalRConnectionsProvider',
    );
  }
  return context;
};

export type SignalRConnectionsContextType = {
  createConnection: (
    hubName: string,
    queryParameters?: string[],
    setIsConnected?: Dispatch<SetStateAction<boolean>>,
  ) => SignalRConnectionData;
  setupEventHandlers: <T>(
    hubName: string,
    handlers: Partial<T>,
    queryParameters?: string[],
  ) => void;
  deleteConnection: <T>(
    hubName: string,
    handlersToRemove: Partial<T>,
    setIsConnected?: Dispatch<SetStateAction<boolean>>,
  ) => void;
  connectionsStore: RefObject<Map<string, SignalRConnectionData>>;
};

export const SignalRConnectionsContext =
  createContext<SignalRConnectionsContextType | null>(null);
